import { call, put, takeEvery, select } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../api/service/apiService';
import type { ApiResponse } from '../../api/service/apiService';
import { PROMOFLOW_BASE_API_URL, ENDPOINTS } from '../../api/endPoints';
import {
  fetchIntegrationsRequest,
  fetchIntegrationsSuccess,
  fetchIntegrationsFailure,
  activateIntegrationRequest,
  activateIntegrationSuccess,
  activateIntegrationFailure,
  deactivateIntegrationRequest,
  deactivateIntegrationSuccess,
  deactivateIntegrationFailure,
  facebookAuthCallbackRequest,
  facebookAuthCallbackSuccess,
  facebookAuthCallbackFailure,
  updateTikTokSettingsRequest,
  updateTikTokSettingsSuccess,
  updateTikTokSettingsFailure,
} from '../slices/integrationsSlice';

import { fetchFacebookPagesRequest, clearFacebookPages } from '../slices/facebookPagesSlice';
import type { RootState } from '../store';
import type { Integration } from '../slices/integrationsSlice';
import { openOAuthPopup } from '../../utils/oauthHelper';

function* fetchIntegrationsSaga(
  action: PayloadAction<{ restaurantId: string }>
): Generator<any, void, ApiResponse<Integration[]>> {
  try {
    const { restaurantId } = action.payload;
    const endpoint = ENDPOINTS.integrations.fetchAllIntegration();
    const response: ApiResponse<any> = yield call(apiService.get, {
      endpoint,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    const integrations = response.data?.data || response.data || [];
    yield put(fetchIntegrationsSuccess(integrations));
  } catch (error: any) {
    yield put(
      fetchIntegrationsFailure(error.message || 'Failed to fetch integrations')
    );
  }
}

function* activateIntegrationSaga(
  action: PayloadAction<{ platform: string; restaurantId: string; fromName?: string }>
): Generator<any, void, any> {
  try {
    const { platform, restaurantId, fromName } = action.payload;

    // For email, use the activate endpoint
    if (platform.toLowerCase() === 'email') {
      const endpoint = ENDPOINTS.integrations.activateEmail();

      yield call(apiService.post, {
        endpoint,
        body: {
          fromName: fromName || '',
        },
        headers: {
          'restaurant-id': restaurantId,
        },
      });

      // After activation, fetch only the email integration data
      const emailEndpoint = ENDPOINTS.integrations.get('email');
      const emailResponse: ApiResponse<any> = yield call(apiService.get, {
        endpoint: emailEndpoint,
        headers: {
          'restaurant-id': restaurantId,
        },
      });

      yield put(activateIntegrationSuccess());

      // Update only the email integration in state
      const emailData = emailResponse.data?.data || emailResponse.data;
      if (emailData) {
        yield put(facebookAuthCallbackSuccess(emailData));
      }
    } else if (platform.toLowerCase() === 'sms') {
      const endpoint = ENDPOINTS.integrations.activateSms();

      yield call(apiService.post, {
        endpoint,
        headers: {
          'restaurant-id': restaurantId,
        },
      });

      yield put(activateIntegrationSuccess());
      // Refetch integrations so the SMS integration shows as connected on the details page
      yield put(fetchIntegrationsRequest({ restaurantId }));
    } else {
      // For other platforms, use OAuth popup
      const authorizeUrl = ENDPOINTS.integrations.authorize(platform as any);
      if (!authorizeUrl) {
        throw new Error(`Authorization not configured for ${platform}`);
      }

      yield call(openOAuthPopup, {
        url: authorizeUrl,
        platform,
        onSuccess: () => {
          console.log(`${platform} OAuth flow completed`);
        },
        onError: (error: string) => {
          console.error(`${platform} OAuth error:`, error);
        },
      });

      yield put(activateIntegrationSuccess());
      // Don't fetch integrations here - wait for OAuth callback
    }
  } catch (error: any) {
    yield put(
      activateIntegrationFailure(
        error.message || `Failed to activate ${action.payload.platform}`
      )
    );
  }
}

function* deactivateIntegrationSaga(
  action: PayloadAction<{ restaurantId: string; integrationId: string; platform: string }>
): Generator<any, void, any> {
  try {
    const { restaurantId, integrationId, platform } = action.payload;
    const endpoint = ENDPOINTS.integrations.delete(platform as any, integrationId);

    if (!endpoint) {
      throw new Error(`Delete endpoint not configured for ${platform}`);
    }

    yield call(apiService.delete, {
      endpoint,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    yield put(deactivateIntegrationSuccess({ integrationId }));

    if (platform.toLowerCase() === 'facebook' ||platform.toLowerCase() === 'instagram') {
      yield put(clearFacebookPages());
    }

  } catch (error: any) {
    yield put(
      deactivateIntegrationFailure(
        error.message || 'Failed to deactivate integration'
      )
    );
  }
}

function* facebookAuthCallbackSaga(
  action: PayloadAction<{ code: string; state: string; error: string | null; platform?: string;}>
): Generator<any, void, any> {
  try {
    const { code, state, error, platform = 'facebook' } = action.payload;

    if (error) {
      throw new Error(error);
    }

    const restaurantId: string = yield select(
      (state: RootState) => state.clover.restaurantId
    );

    if (!restaurantId) {
      throw new Error('Restaurant ID not found');
    }

    const authenticateEndpoint = ENDPOINTS.integrations.authenticate(platform as any);

    if (!authenticateEndpoint) {
      throw new Error(`Authenticate endpoint not configured for ${platform}`);
    }

    const response: ApiResponse<any> = yield call(apiService.post, {
      endpoint: authenticateEndpoint,
      body: {
        code,
        state,
      },
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    const integrationData = response.data?.data || response.data;
    const integrationId = integrationData?.id;

    yield put(facebookAuthCallbackSuccess(integrationData));
    yield put(fetchIntegrationsRequest({ restaurantId }));

    if (integrationId && (platform === 'facebook' || platform === 'instagram')) {
      yield put(fetchFacebookPagesRequest({ integrationId }));
    }
  } catch (error: any) {
    console.error('Auth callback error:', error);
    yield put(
      facebookAuthCallbackFailure(
        error.message || 'Failed to complete authentication'
      )
    );
  }
}

function* updateTikTokSettingsSaga(
  action: PayloadAction<{
    integrationId: string;
    restaurantId: string;
    settings: {
      disable_duet: boolean;
      disable_stitch: boolean;
      disable_comment: boolean;
      brand_content_toggle: boolean;
      brand_organic_toggle: boolean;
      is_aigc?: boolean;
      privacyLevel: string;
    };
  }>
): Generator<any, void, any> {
  try {
    const { integrationId, restaurantId, settings } = action.payload;
    const endpoint =ENDPOINTS.integrations.updateTikTokSettings(integrationId);

    const response: ApiResponse<any> = yield call(apiService.put, {
      endpoint,
      body: settings,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    const integrationData = response.data?.data || response.data;

    yield put(updateTikTokSettingsSuccess(integrationData));
  } catch (error: any) {
    yield put(
      updateTikTokSettingsFailure(
        error.message || 'Failed to update TikTok settings'
      )
    );
  }
}

export function* integrationsSaga(): Generator<any, void, any> {
  yield takeEvery(fetchIntegrationsRequest.type, fetchIntegrationsSaga);
  yield takeEvery(activateIntegrationRequest.type, activateIntegrationSaga);
  yield takeEvery(deactivateIntegrationRequest.type, deactivateIntegrationSaga);
  yield takeEvery(facebookAuthCallbackRequest.type, facebookAuthCallbackSaga);
  yield takeEvery(updateTikTokSettingsRequest.type, updateTikTokSettingsSaga);
}

export default integrationsSaga;
